import AOS from "aos";
import "aos/dist/aos.css";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import EmailSvg from "../../assets/images/email_svg.svg";
import LocationSvg from "../../assets/images/location_svg.svg";
import PhoneSvg from "../../assets/images/phone_svg.svg";
import Cursor from "../../common/Cursor";
import Footer from "../../common/Footer";
import Header from "../../common/Header";
import Loader from "../../common/Loader";
import TextMarqueSection from "../../common/TextMarqueSection";
import { postJSON } from "../../api/api";

const initialForm = {
  name: "",
  email: "",
  mobile: "",   // 🔁 phone → mobile to match backend
  subject: "",
  message: "",
};

const mobileRegex = /^\+?\d{7,15}$/; // same as backend

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  useEffect(() => {
    AOS.init({ duration: 2000 });
  }, []);

  const onChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  // Required per model: name, email, mobile
  const validate = () => {
    if (!form.name.trim()) return "Name is required";
    if (!form.email.trim()) return "Email is required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Enter a valid email";
    if (!form.mobile.trim()) return "Mobile is required";
    if (!mobileRegex.test(form.mobile))
      return "Enter a valid mobile (7–15 digits, optional +)";
    // subject/message are optional in your model
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });
    const err = validate();
    if (err) {
      setMsg({ type: "error", text: err });
      return;
    }
    try {
      setSubmitting(true);
      await postJSON("/contact/create/", {
        name: form.name,
        email: form.email,
        mobile: form.mobile,               // ✅ send mobile (not phone)
        subject: form.subject || null,     // optional
        message: form.message || null,     // optional
      });
      setMsg({
        type: "success",
        text: "Thanks! Your message has been sent. We’ll get back to you soon.",
      });
      setForm(initialForm);
    } catch (error) {
      setMsg({
        type: "error",
        text:
          error?.message?.includes("HTTP 400")
            ? "Validation failed. Please check your details and try again."
            : "Sorry, we couldn’t send your message. Please try again in a moment.",
      });
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Marichi Tech AI</title>
        <meta
          name="description"
          content="Get in touch with us to start building your digital product or get support."
        />
        <meta
          name="keywords"
          content="Contact, support, IT consultation, get in touch"
        />
        <meta property="og:title" content="Contact | Marichi Tech AI" />
        <meta
          property="og:description"
          content="We're here to help. Contact us for any inquiries or project discussions."
        />
        <meta
          property="og:image"
          content="https://example.com/contact-og-image.jpg"
        />
      </Helmet>

      {submitting && <Loader />}
      <div className="site_contain">
        <Cursor />
        <Header />

        <section
          className="section-main contact-section-main overflow-hidden"
          style={{ paddingBottom: "6rem" }}
          id="section-main-ids"
        >
          <div className="container">
            <p className="main-text" data-aos="fade-down">
              CONTACT US
            </p>
            <h1 className="JessicaBiogi blog2-main-text" data-aos="fade-down">
              Let's make something awesome together!
            </h1>
          </div>
        </section>

        <section className="section-two-my-services">
          <div
            className="container"
            id="form-conatiners"
            style={{ marginTop: "-3rem" }}
          >
            <div className="row" id="forms">
              <div className="col-xxl-8 col-xl-8 col-lg-8 ">
                <form className="form-main" onSubmit={onSubmit} noValidate>
                  <div className="form-main-sub">
                    <div className="input-main-form">
                      <p className="name">Name:</p>
                      <input
                        type="text"
                        name="name"
                        placeholder="Your name"
                        autoComplete="name"
                        value={form.name}
                        onChange={onChange}
                        required
                      />
                    </div>
                    <div className="input-main-form">
                      <p className="name">Email:</p>
                      <input
                        type="email"
                        name="email"
                        placeholder="Your email"
                        autoComplete="email"
                        value={form.email}
                        onChange={onChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-main-sub">
                    <div className="input-main-form">
                      <p className="name">Mobile:</p>
                      <input
                        type="tel"
                        name="mobile" // 🔁 name changed
                        placeholder="+97798xxxxxxx"
                        autoComplete="tel"
                        value={form.mobile}
                        onChange={onChange}
                        required
                        pattern="^\+?\d{7,15}$"
                        title="7–15 digits, optional leading +"
                      />
                    </div>
                    <div className="input-main-form">
                      <p className="name">Subject (optional):</p>
                      <input
                        type="text"
                        name="subject"
                        placeholder="Your subject"
                        autoComplete="off"
                        value={form.subject}
                        onChange={onChange}
                      />
                    </div>
                  </div>

                  <div className="input-main-form">
                    <p className="name">Message (optional):</p>
                    <textarea
                      name="message"
                      placeholder="Type your message..."
                      value={form.message}
                      onChange={onChange}
                      rows={6}
                    />
                  </div>

                  {msg.text && (
                    <p
                      style={{
                        color: msg.type === "error" ? "#ef4444" : "#10b981",
                        marginTop: 8,
                        marginBottom: 0,
                        fontWeight: 500,
                      }}
                    >
                      {msg.text}
                    </p>
                  )}

                  <div className="form-submit-btn-main" style={{ marginTop: 12 }}>
                    <button
                      className="explore-btn hover-slide-right form-submit-btn"
                      type="submit"
                      disabled={submitting}
                      aria-busy={submitting}
                    >
                      <span id="toggle">
                        {submitting ? "SENDING..." : "SUBMIT"}
                        <svg
                          className="expo-arrow2"
                          xmlns="http://www.w3.org/2000/svg"
                          width="32"
                          height="32"
                          viewBox="0 0 32 32"
                          fill="none"
                        >
                          <g>
                            <path
                              d="M6.66669 16H25.3334"
                              stroke="#fff"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M20 21.3333L25.3333 16"
                              stroke="#fff"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M20 10.6666L25.3333 16"
                              stroke="#fff"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </g>
                        </svg>
                      </span>
                    </button>
                  </div>
                </form>
              </div>

              <div className="col-xxl-4 col-xl-4 col-lg-4 overflow-hidden">
                <div className="get-touch-box-main">
                  <h2>Get In Touch</h2>
                  <p className="well">
                    When do they work well, and when do they on us and finally,
                    when do we actually need how can we avoid them.
                  </p>
                  <div className="contact-footer contact-footer2">
                    <img src={PhoneSvg} alt="phone" />
                    <a href="tel:+9779840540455">+977 9840540455</a>
                  </div>
                  <div className="contact-footer contact-footer2">
                    <img src={EmailSvg} alt="email" />
                    <a href="mailto:info@marichitechai.com">
                      info@marichitechai.com
                    </a>
                  </div>
                  <div className="contact-footer contact-footer2">
                    <img src={LocationSvg} alt="location" />
                    <p>Lazimpat, Nepal</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="container" style={{ position: "relative" }}>
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1765.948478770348!2d85.3253255!3d27.7204676!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39eb19435db1a873%3A0xdc2d1526639b979e!2sMarichi%20Tech%20AI%20Pvt.%20Ltd.!5e0!3m2!1sen!2suk!4v1748792813466!5m2!1sen!2suk"
            width="100%"
            height="450"
            style={{ border: 0, marginTop: "4rem", borderRadius: "10px" }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Marichi Tech AI Location"
          />
        </div>

        <TextMarqueSection />
        <Footer />
      </div>
    </>
  );
};

export default Contact;
